import React from 'react';
import { Portal } from '@chakra-ui/react';

/**
 * Where a menu or popover in the chat should render.
 *
 * ★ INSIDE THE LEAD DRAWER, MENUS RENDER INLINE — NO PORTAL AT ALL.
 * On the inbox page a menu portals to `<body>` so it can escape the thread's
 * `overflow: hidden`. Inside the drawer that same portal landed BEHIND the
 * drawer (Chakra mounts a drawer on `<body>` at z-index 1400, and a sibling
 * portal has no stacking context of its own) — "I click the paperclip and
 * nothing shows". Two fixes were then layered on top of the portal: a ref to
 * the drawer's surface that Chakra never delivered, and a node held in state
 * with a zero-size anchor box. Both worked; both were more machinery than the
 * problem deserved.
 *
 * Rendering inline removes the whole question. The menu is a descendant of
 * the drawer, so it paints above the drawer's content because it is inside
 * it, the focus trap keeps it because nothing left the drawer, and there is
 * no target to fetch, no ref to lose and no retry to get wrong. The attach
 * and message menus open UPWARD from the composer over the message list —
 * well inside the drawer body — so its `overflow: hidden` never clips them.
 *
 * `strategy: 'absolute'` still matters inline. Chakra animates the drawer
 * with a transform, and a transformed ancestor becomes the containing block
 * for `position: fixed` descendants: Popper's fixed strategy would compute
 * viewport coordinates and land the menu off-screen (measured: x=988 on a
 * 1024px window). Absolute positions against the offset parent, which is
 * inside the drawer.
 */
const InDrawerContext = React.createContext(false);

/** Wrap the drawer's content in this; nothing else has to be threaded down. */
export function PortalTargetProvider({ children }) {
	return <InDrawerContext.Provider value>{children}</InDrawerContext.Provider>;
}

/** Is this chat rendered inside the lead WhatsApp drawer? */
export function useInDrawer() {
	return React.useContext(InDrawerContext);
}

/** Which way Popper should position a menu or popover in this chat. */
export function useMenuStrategy() {
	return useInDrawer() ? 'absolute' : 'fixed';
}

/** Inline inside the drawer; a `<body>` portal everywhere else. */
export function MenuPortal({ children }) {
	if (useInDrawer()) return children;
	return <Portal>{children}</Portal>;
}

export default MenuPortal;
